import { useMutation, useQuery } from '@tanstack/react-query';
import { FormApi } from 'final-form';
import arrayMutators from 'final-form-arrays';
import * as R from 'ramda';
import React, { useCallback, useMemo, useState } from 'react';
import { Form } from 'react-final-form';
import toast from 'react-hot-toast';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import ErrorToastContent from 'common/components/ErrorToastContent';
import { localize } from 'common/utils';
import validateYupSchema from 'common/utils/validateYupSchema';
import { useCountryConfigContext } from 'modules/country/CountryConfigContext';
import { MultiImageValue } from 'modules/menu/components/ImageUploadInput/MultiImageUploadInput';
import MenuEditorConfirmationPrompt from 'modules/menu/components/MenuEditorConfirmationPrompt';
import { initialVatFieldValue, useVatCategories } from 'modules/menu/components/VatCategories';
import { getModifiedFieldsForTracking, useCreateItemTracking } from 'modules/menu/hooks/telemetryHooks';
import {
  createOffer,
  createOfferBinding,
  getProductInformationSettings,
  updateVenueInventory,
} from 'modules/shared-menu/api';
import { useOfferingMenuFeaturesContext } from 'modules/shared-menu/contexts/OfferingMenuFeaturesContext';
import { useScopeContext } from 'modules/shared-menu/contexts/ScopeContext';
import { useListingManagerRoutes } from 'modules/shared-menu/hooks/useListingManagerRoutes';

import { OfferDetailsFormFields, ValidationContext, FormValues, validationSchema } from './OfferDetailsForm';
import {
  transformOfferApiToFormValues,
  buildNewOfferFormValues,
  transformOfferFormValuesToApi,
} from './OfferDetailsForm/transformers';
import { OfferStickyFooter } from './OfferStickyFooter';
import { cleanUpInvalidPrerequisiteOptionChoices } from './OptionBindings/utils';
import { buildCategoryLink } from './dnd/offers/useDndMethods';
import { useSelectionData } from '../../../hooks/useSelectionData';
import { SuccessToast } from '../../SharedMenuPage/components/CentralisedAddButton';
import { WOLT_SPECIALS_CATEGORY_ID } from '../constants';
import { useProcessSelectionImage } from '../hooks/useProcessSelectionImage';
import { Offer } from '../types';

type Props = {
  initialCategoryId: string | null;
  itemToCopy?: Offer;
  onSuccess: () => void;
  onClose: () => void;
};

type CreateOfferMutationVariables = {
  selectionId: string;
  payload: Offer;
};

type CreateOfferBindingMutationVariables = {
  selectionId: string;
  categoryId: string;
  offerId: string;
};

type UpdateOfferInventoryVariables = {
  offerId: string;
  total: number;
};

const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
`;

const StyledOfferDetailsFormFields = styled(OfferDetailsFormFields)`
  flex: 1 1 auto;
  overflow: auto;
  padding: 0 24px;
`;

export const AddOrCopyOffer = ({ initialCategoryId, itemToCopy, onClose, onSuccess }: Props) => {
  const menuFeatures = useOfferingMenuFeaturesContext();
  const { currency, alcohol_warning_permille_limit: alcoholWarningPermilleLimit } = useCountryConfigContext();
  const { menuEditorPage } = useListingManagerRoutes();
  const { data: vatCategories } = useVatCategories();
  const { selection, categories, updateSelectionData, options } = useSelectionData();
  const { trackAddItem, trackCopyItem, trackCreateSpecial } = useCreateItemTracking();
  const scope = useScopeContext();

  const processSelectionImage = useProcessSelectionImage();

  const { data: productInfoSettings } = useQuery(
    ['product-information-settings', selection.id],
    getProductInformationSettings,
    { refetchOnMount: false, refetchOnWindowFocus: false },
  );

  const initialFormValues = useMemo(() => {
    if (itemToCopy) {
      return transformOfferApiToFormValues(
        { ...itemToCopy, inventory_mode: undefined },
        {
          alcoholWarningPermilleLimit,
          language: selection.primary_language,
          categoryId: initialCategoryId,
          vatCategories: vatCategories?.list,
          productInformationSettings: productInfoSettings,
        },
      );
    }
    return buildNewOfferFormValues(
      initialCategoryId,
      initialVatFieldValue(vatCategories?.list, vatCategories?.default?.code),
      productInfoSettings?.nutrition_values,
    );
  }, [
    alcoholWarningPermilleLimit,
    initialCategoryId,
    itemToCopy,
    selection.primary_language,
    vatCategories,
    productInfoSettings,
  ]);

  const initialNutritionFactsFormat = useMemo(() => {
    if (!itemToCopy) {
      return 'table';
    }
    return initialFormValues.nutritionValues.some(n => n.value !== undefined) ? 'table' : 'text';
  }, [initialFormValues.nutritionValues, itemToCopy]);

  const [nutritionFactsFormat, setNutritionFactsFormat] = useState<'text' | 'table'>(initialNutritionFactsFormat);

  const validationContext = useMemo<ValidationContext>(() => ({ currency, currOverriddenRowGroups: [] }), [currency]);

  const showSuccessNotification = useCallback(
    (categoryId: string | null, offer: Offer) => {
      const offerLink = categoryId
        ? `${buildCategoryLink(menuEditorPage(), categoryId, categories)}?item=${offer.id}`
        : `${menuEditorPage()}/no-category?item=${offer.id}`;

      toast.success(
        <SuccessToast
          link={offerLink}
          linkLabel={<FormattedMessage id="shared-menu.editor.notifications.create.offer.success.link-label" />}
          message={
            <FormattedMessage
              id="shared-menu.editor.notifications.create.offer.success.message"
              values={{
                name: <strong>{localize(offer.name, selection.primary_language)}</strong>,
              }}
            />
          }
        />,
      );
    },
    [categories, menuEditorPage, selection.primary_language],
  );

  const { mutateAsync: executeProcessSelectionImage } = useMutation((image: MultiImageValue) =>
    processSelectionImage(image),
  );

  const { mutateAsync: executeCreateOffer } = useMutation(
    ({ selectionId, payload }: CreateOfferMutationVariables) => createOffer(selectionId, payload),
    {
      onSuccess: newOffer => {
        updateSelectionData({ actionType: 'addOffer', payload: newOffer });
      },
    },
  );

  const { mutateAsync: executeCreateOfferBinding } = useMutation(
    ({ selectionId, categoryId, offerId }: CreateOfferBindingMutationVariables) =>
      createOfferBinding(selectionId, categoryId, offerId),
    {
      onSuccess: updatedSelection => {
        updateSelectionData({ actionType: 'updateSelection', payload: updatedSelection });
      },
    },
  );

  const { mutateAsync: executeUpdateOfferInventory } = useMutation(
    ({ total, offerId }: UpdateOfferInventoryVariables) => updateVenueInventory(scope.id, total, offerId),
  );

  const handleSubmit = async (formValues: FormValues, form: FormApi<FormValues, Partial<FormValues>>) => {
    const validValues = validationSchema.validateSync(formValues, { context: validationContext });

    let offer: Offer | undefined;
    try {
      const imageUrls = await Promise.all(
        validValues.image.map(async image => {
          return await executeProcessSelectionImage(image);
        }),
      );
      const definedImageUrls = imageUrls.filter((url: string | null): url is string => url !== null);

      const haveOptionBindingsChanged = !R.equals(initialFormValues.optionBindings, validValues.optionBindings);
      const optionBindings = haveOptionBindingsChanged
        ? cleanUpInvalidPrerequisiteOptionChoices(
            validValues.optionBindings ?? [],
            initialFormValues.optionBindings,
            options,
          )
        : validValues.optionBindings;

      const validValuesWithCleanedUpOptionBindings = {
        ...validValues,
        optionBindings,
      };

      const payload = transformOfferFormValuesToApi(
        itemToCopy ?? null,
        validValuesWithCleanedUpOptionBindings,
        definedImageUrls,
        {
          language: selection.primary_language,
          currency,
          nutritionFactsFormat,
          productHierarchies: productInfoSettings?.product_hierarchy_levels ?? [],
          externalInfoHyperlinkAllowed: menuFeatures.externalInfoHyperlinkAllowed,
        },
      );

      offer = await executeCreateOffer({ selectionId: selection.id, payload });

      const isBinaryInventory = !offer.inventory_mode || offer.inventory_mode === 'forced_out_of_stock';
      if (!isBinaryInventory && validValues.inventoryBalance !== undefined) {
        try {
          await executeUpdateOfferInventory({ offerId: offer.id, total: validValues.inventoryBalance });
        } catch (e) {
          toast.error(<ErrorToastContent error={e} />, { duration: Infinity });
        }
      }

      const modifiedFields = getModifiedFieldsForTracking<FormValues>(form.getState());
      const isWoltSpecial = initialFormValues.categoryId === WOLT_SPECIALS_CATEGORY_ID;

      if (isWoltSpecial) {
        trackCreateSpecial(modifiedFields);
      } else if (!!itemToCopy) {
        trackCopyItem(modifiedFields);
      } else {
        trackAddItem(offer?.id ?? '', !!imageUrls.length, validValues.mandatoryWarnings, modifiedFields);
      }
    } catch (error) {
      toast.error(<ErrorToastContent error={error} />, { duration: Infinity });
      return;
    }

    if (validValues.categoryId !== null) {
      try {
        await executeCreateOfferBinding({
          selectionId: selection.id,
          categoryId: validValues.categoryId,
          offerId: offer.id,
        });
      } catch (error) {
        toast.error(<ErrorToastContent error={error} />, { duration: Infinity });
      }
    }

    onSuccess();
    showSuccessNotification(validValues.categoryId, offer);
  };

  return categories ? (
    <Form<FormValues>
      initialValues={initialFormValues}
      onSubmit={handleSubmit}
      mutators={{ ...arrayMutators }}
      validate={validateYupSchema(validationSchema, validationContext)}
    >
      {({ handleSubmit, submitting, dirty }) => (
        <FormContainer onSubmit={handleSubmit}>
          <StyledOfferDetailsFormFields
            sidePanelMode="create"
            primaryLanguage={selection.primary_language}
            onChangeNutritionFactsFormat={setNutritionFactsFormat}
            nutritionFactsFormat={nutritionFactsFormat}
            productInfoSettings={productInfoSettings}
            offer={itemToCopy}
            key={itemToCopy?.id}
          />
          <OfferStickyFooter onClose={onClose} onSubmit={handleSubmit} isLoading={submitting} />
          <MenuEditorConfirmationPrompt when={dirty && !submitting} />
        </FormContainer>
      )}
    </Form>
  ) : null;
};
